import { InfoCircleOutlined } from "@ant-design/icons";
import {
  Breadcrumb,
  Button,
  Divider,
  Empty,
  Input,
  Layout,
  Result,
  Skeleton,
  Table,
  notification,
  theme,
} from "antd";
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiListAdminEvents } from "../../../../api/events";
import { EMPTY_VALUE, formatDateTime } from "../../../../utils/format";
import "./Events.scss";

const { Content } = Layout;

const Events = () => {
  const navigate = useNavigate();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const [filteredData, setFilteredData] = useState([]);

  const handleSearch = async (value) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    setLoading(true);
    try {
      if (value === "") {
        await handleSimulate(pagination.current);
      } else {
        const json = await apiListAdminEvents({ page: 1, search: value });
        setFilteredData(json.results);
        setPagination((prevPagination) => ({
          ...prevPagination,
          total: json.count,
          current: 1,
        }));
      }
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Ocorreu um erro ao buscar os dados.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const [pagination, setPagination] = useState({
    current: 1,
    total: 0,
    pageSize: 10,
  });

  const [loading, setLoading] = useState(true);

  const [activeError, setActiveError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");

  const renderError = (status) => {
    if (activeError) {
      return (
        <Result
          status={status === "500" ? "500" : "404"}
          title={status}
          subTitle={
            status === "500"
              ? "Ocorreu um erro no servidor, tente novamente mais tarde."
              : "Ocorreu um erro, tente novamente mais tarde."
          }
          extra={
            <Button onClick={() => navigate("/")} type="primary">
              Dashboard
            </Button>
          }
        />
      );
    }
  };

  const handleSimulate = async (page) => {
    setLoading(true);
    try {
      const json = await apiListAdminEvents({ page });
      setFilteredData(json.results);
      setPagination((prevPagination) => ({
        ...prevPagination,
        total: json.count,
        current: page,
      }));
    } catch (error) {
      setLoading(false);
      setActiveError(true);
      if (error?.status === 400) {
        notification.error({
          message: "Erro",
          description: "Ocorreu um erro ao listar os eventos.",
          placement: "topRight",
        });
        setErrorStatus("404");
      } else if (error?.status === 500) {
        notification.error({
          message: "Erro",
          description:
            "Ocorreu um erro no servidor ao listar os eventos. Por favor, tente novamente mais tarde.",
          placement: "topRight",
        });
        setErrorStatus("500");
      } else {
        setErrorStatus("404");
        notification.error({
          message: "Erro",
          description: "Erro ao buscar os eventos.",
          placement: "topRight",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const onChange = (pagination) => {
    handleSimulate(pagination.current);
  };

  useEffect(() => {
    handleSimulate(pagination.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current]);

  const searchTimeout = useRef(null);

  const handleSearchDebounced = (value) => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => handleSearch(value), 300);
  };

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  const columns = [
    {
      title: "Nome",
      dataIndex: "name",
      key: "name",
      onCell: () => ({
        style: { cursor: "pointer" },
      }),
      render: (name) => <p style={{ textTransform: "capitalize" }}>{name}</p>,
    },
    {
      title: "Responsável",
      dataIndex: ["account", "display_name"],
      key: "account",
      render: (displayName) => <p>{displayName || EMPTY_VALUE}</p>,
      responsive: ["md"],
    },
    {
      title: "Data de Início",
      dataIndex: "starts_at",
      key: "starts_at",
      render: (date) => <p>{formatDateTime(date) || '-'}</p>,
      responsive: ["sm"],
    },
    {
      title: "Data de Término",
      dataIndex: "ends_at",
      key: "ends_at",
      render: (date) => <p>{formatDateTime(date) || '-'}</p>,
      responsive: ["sm"],
    },
    {
      title: "Ingressos Vendidos",
      dataIndex: "tickets_sold",
      key: "tickets_sold",
      render: (value) => <p>{value || 0}</p>,
      responsive: ["sm"],
    },
    {
      title: "",
      dataIndex: "",
      render: (record) => (
        <Link to={`/eventos/${record.account?.id}/${record.id}`}>
          <InfoCircleOutlined style={{ cursor: "pointer", height: "100%" }} />{" "}
        </Link>
      ),
      key: "",
    },
  ];

  const locale = {
    emptyText: <Empty description="Sem dados" />,
  };

  return (
    <div className="Events">
      {activeError ? (
        renderError(errorStatus)
      ) : (
        <>
          <Layout
            style={{
              padding: "1.25rem",
            }}
          >
            <Breadcrumb
              style={{
                margin: "16px 0",
              }}
            >
              <div style={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
                <h2 className="title-page">Eventos</h2>
              </div>
              <Divider />
              <Breadcrumb.Item>Eventos</Breadcrumb.Item>
            </Breadcrumb>
            <Content
              style={{
                padding: 24,
                margin: 0,
                minHeight: 280,
                background: colorBgContainer,
                borderRadius: borderRadiusLG,
              }}
            >
              <div className="box-divider">
                <h2 className="subtitle-page">Lista de eventos</h2>
                <div className="button-group">
                  <Button onClick={() => navigate("/eventos/compras")}>
                    Buscar compras
                  </Button>
                  <Button
                    onClick={() => navigate("/eventos/criar")}
                    type="primary"
                  >
                    Criar Evento
                  </Button>
                </div>
              </div>
              <Divider />
              <Input.Search
                placeholder='Pesquisar'
                allowClear
                enterButton
                onSearch={handleSearch}
                onChange={(e) => handleSearchDebounced(e.target.value)}
                style={{ marginBottom: 16, maxWidth: '200px' }}
              />
              {loading ? (
                <Skeleton active />
              ) : (
                <Table
                  columns={columns}
                  locale={locale}
                  dataSource={
                    filteredData &&
                    filteredData.map((item) => ({ ...item, key: item.id }))
                  }
                  pagination={{
                    current: pagination.current,
                    total: pagination.total,
                    showSizeChanger: false,
                  }}
                  bordered
                  onChange={onChange}
                />
              )}
            </Content>
            <Divider />
          </Layout>
        </>
      )}
    </div>
  );
};

export default Events;
